import { useMatch } from 'react-router';
import { Link } from 'react-router';
import { PanelLeftClose, Plus } from 'lucide-react';
import { Button, IconButton, Skeleton, Wordmark, buttonVariants } from '@/components/ui';
import { useChats } from '@/features/chat/api';
import { groupChats } from '@/lib/format';
import { cn } from '@/lib/cn';
import { ChatListItem } from './ChatListItem';
import { UserMenu } from './UserMenu';

export function SidebarContent({ onNavigate, onCollapse }: { onNavigate?: () => void; onCollapse?: () => void }) {
  const chats = useChats();
  const match = useMatch('/c/:chatId');
  const activeId = match?.params.chatId;
  const groups = chats.data ? groupChats(chats.data) : [];

  return (
    <div className="flex h-full w-full flex-col bg-sidebar pt-[var(--safe-top)]">
      <div className="flex items-center justify-between px-4 pb-2 pt-4">
        <Link to="/" onClick={onNavigate} aria-label="MedicAiL home" className="rounded-xl">
          <Wordmark />
        </Link>
        {onCollapse && (
          <IconButton label="Close sidebar" size="icon-sm" onClick={onCollapse} className="text-ink-muted">
            <PanelLeftClose className="size-[18px]" />
          </IconButton>
        )}
      </div>

      <div className="px-3 pb-3 pt-2">
        <Link to="/" onClick={onNavigate} className={cn(buttonVariants({ variant: 'navy', size: 'lg' }), 'w-full justify-start gap-2.5 rounded-2xl')}>
          <Plus className="size-5" aria-hidden="true" /> New chat
        </Link>
      </div>

      <nav aria-label="Chat history" className="scroll-thin min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {chats.isPending && (
          <div className="space-y-2 pt-2" aria-busy="true" aria-label="Loading chats">
            {[70, 90, 55, 80, 65].map((w, i) => (
              <Skeleton key={i} className="h-9 rounded-xl" style={{ width: `${w}%` }} />
            ))}
          </div>
        )}
        {chats.isError && (
          <div className="rounded-2xl bg-surface-muted p-4 text-center text-sm text-ink-muted">
            Could not load your chats.
            <Button variant="outline" size="sm" className="mt-3" onClick={() => void chats.refetch()}>
              Try again
            </Button>
          </div>
        )}
        {chats.isSuccess && groups.length === 0 && (
          <p className="px-3 pt-4 text-center text-sm leading-relaxed text-ink-muted">Your conversations will appear here.</p>
        )}
        {groups.map((g) => (
          <section key={g.label} aria-label={g.label} className="mb-3">
            <h2 className="px-3 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-ink-muted">{g.label}</h2>
            <ul className="space-y-0.5">
              {g.chats.map((c) => (
                <ChatListItem key={c.chatId} chat={c} active={c.chatId === activeId} onNavigate={onNavigate} />
              ))}
            </ul>
          </section>
        ))}
      </nav>

      <div className="border-t border-line p-2 pb-[max(0.5rem,var(--safe-bottom))]">
        <UserMenu onNavigate={onNavigate} />
      </div>
    </div>
  );
}
